import { BusyIndicator } from "client/Components";
import { DataSourceSortableState } from "client/Data/sorting";
import {
	AlertManager,
	createLocalDataSource,
	useAlerts,
	useSelection,
	useSortHandler
} from "client/Hooks";
import {
	DocumentItemType,
	DocumentsRecycleBinItem,
	DocumentsViewModel,
	DocumentsViewType,
	ListLocationViewModel
} from "client/Api/Documents/SharePoint/common";
import { getRecycleBinItems } from "client/Api/Documents/SharePoint/sharePointRecycleBin";
import {
	Dispatch,
	ReactElement,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	Action,
	ActionKind,
	ActionsWithContext
} from "client_legacy/ndaw/actions";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import { SharePointRecycleBinTableView } from "./SharePointRecycleBinTableView";
import { getExtension } from "client_legacy/Documents/path";
import { SharePointRecycleBinListView } from "./SharePointRecycleBinListView";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { DocumentLayoutKind } from "client/Api/Documents/documents";
import { SharePointRecycleBinGridView } from "./SharePointRecycleBinGridView";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { useRecoverFromRecyclebin } from "./Actions/useRecoverFromRecyclebin";
import { useDeleteFromRecyclebin } from "./Actions/useDeleteFromRecyclebin";

type LoadResult = TryResult<readonly GridItem[]>;

export type GridItem = {
	id: Guid;
	name: string;
	extension: string | null;
	originalLocation: string;
	deletedOn: Date;
	size: number;
	model: DocumentsRecycleBinItem;
};

const useDataSource = createLocalDataSource<GridItem>()
	.setConfiguration({})
	.getHook();

export type SharePointRecycleBinProps = {
	view: DocumentsViewModel;
	sortingState: DataSourceSortableState<GridItem>;
	setSortingState: Dispatch<SetStateAction<DataSourceSortableState<GridItem>>>;
	isMobile: boolean;
	layoutKind: DocumentLayoutKind;
	onActionsChanged: (
		value: ActionsWithContext<RecycleBinActionContext, Action<RecycleBinActionContext>>
	) => (() => void);
};

export const SharePointRecycleBin = (props: SharePointRecycleBinProps): ReactElement => {
	const {
		view,
		sortingState,
		setSortingState,
		isMobile,
		layoutKind,
		onActionsChanged
	} = props;

	if (view.type !== DocumentsViewType.RecycleBinView) {
		throw new Error("Must be a recycle bin view");
	}

	const [isBusy, setIsBusy] = useState<boolean>(false);

	const [loadResult, setLoadResult] = useState<LoadResult>({
		wasSuccessful: false
	});

	const { sortingFor } = useSortHandler<GridItem>(
		sortingState,
		setSortingState,
		true
	);

	const [selection, setSelection] = useState<readonly GridItem[]>([]);

	const items = useMemo(
		(): readonly GridItem[] => {
			return loadResult.wasSuccessful
				? loadResult.result
				: [];
		},
		[loadResult]
	);

	const { currentItems: sortedItems } = useDataSource(
		items,
		{
			sorting: sortingState
		}
	);

	const { selectionFor, multiSelection } = useSelection(
		sortedItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems: selection,
			setSelectedItems: setSelection
		}
	);

	const location: ListLocationViewModel = view.location;

	const alertManager: AlertManager = useAlerts();

	const deletedHandler = useCallback(
		(ids: readonly Guid[]): void => {
			setLoadResult(
				(x) => {
					if (!x.wasSuccessful) {
						return x;
					}

					const itemsExcludingDeletedItems: readonly GridItem[] = x.result.filter(
						x => !ids.includes(x.id)
					);

					return {
						...x,
						result: itemsExcludingDeletedItems
					};
				}
			);
		},
		[]
	);

	const [recoverDialog, recoverHandler] = useRecoverFromRecyclebin(
		deletedHandler,
		alertManager
	);

	const [deleteDialog, deleteHandler] = useDeleteFromRecyclebin(
		deletedHandler,
		alertManager
	);

	useEffect(
		(): (() => void) => {
			async function load(
				location: ListLocationViewModel,
				signal: AbortSignal
			): Promise<void> {
				setIsBusy(true);
				try {
					const documents: readonly DocumentsRecycleBinItem[] = await getRecycleBinItems(
						convertToLocationModel(location),
						signal
					);

					setLoadResult({
						wasSuccessful: true,
						result: documents.map(convertToGridItem)
					});
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					setLoadResult({
						wasSuccessful: false
					});

					console.error(e);
					setIsBusy(false);
					alertManager.showError(localize("_UnableToRetrieveItems"));
				}
			}

			const cancellation = new AbortController();
			load(
				location,
				cancellation.signal
			);
			return () => cancellation.abort();
		},
		[location, alertManager]
	);

	const actionCallbacks = useMemo(
		(): ActionHandlers => {
			return {
				recover: recoverHandler,
				delete: deleteHandler,
				selectAll: () => setSelection(sortedItems),
				deselectAll: () => setSelection([])
			};
		},
		[
			deleteHandler,
			recoverHandler,
			sortedItems
		]
	);

	const rootActions: readonly RecycleBinAction[] = useMemo(
		() => getRootActions(actionCallbacks),
		[actionCallbacks]
	);

	const selectionActions: readonly RecycleBinAction[] = useMemo(
		() => getSelectionActions(actionCallbacks),
		[actionCallbacks]
	);

	const actionContext = useMemo(
		(): RecycleBinActionContext => {
			return {
				allItems: items.map(x => x.model),
				selection: selection.map(x => x.model),
				isMobile,
				isEverythingSelected: sortedItems.length === selection.length
			};
		},
		[
			items,
			selection,
			isMobile,
			sortedItems.length
		]
	);

	const allActions = useMemo(
		(): readonly RecycleBinAction[] => [...rootActions, ...selectionActions],
		[rootActions, selectionActions]
	);

	useEffect(
		(): (() => void) => {
			return onActionsChanged({
				context: actionContext,
				actions: allActions
			});
		},
		[
			allActions,
			actionContext,
			onActionsChanged
		]
	);

	const renderContent = (): ReactElement | null => {
		if (!loadResult.wasSuccessful) {
			return null;
		}

		if (!isMobile && layoutKind === DocumentLayoutKind.List) {
			return (
				<SharePointRecycleBinTableView
					items={sortedItems}
					sortingFor={sortingFor}
					multiSelection={multiSelection}
					selectionFor={selectionFor}
					selectionActions={selectionActions}
					actionContext={actionContext}
				/>
			);
		}

		if (layoutKind === DocumentLayoutKind.Grid) {
			return (
				<SharePointRecycleBinGridView
					items={sortedItems}
					multiSelection={multiSelection}
					selectionActions={selectionActions}
					selectionFor={selectionFor}
					actionContext={actionContext}
				/>
			);
		}

		return (
			<SharePointRecycleBinListView
				items={sortedItems}
				selectionFor={selectionFor}
				selectionActions={selectionActions}
				actionContext={actionContext}
			/>
		);
	};

	return (
		<>
			{isBusy && (
				<BusyIndicator />
			)}

			{renderContent()}
			{recoverDialog}
			{deleteDialog}
		</>
	);
};
SharePointRecycleBin.displayName = "SharePointRecycleBin";

function convertToGridItem(document: DocumentsRecycleBinItem): GridItem {
	const {
		guid,
		name,
		originalLocation,
		deletedOn,
		size
	} = document;

	return {
		id: guid,
		name,
		extension: document.itemType === DocumentItemType.File
			? getExtension(name)
			: null,
		originalLocation,
		deletedOn: parseDtoDate(deletedOn),
		size,
		model: document
	};
}

export type RecycleBinActionContext = {
	selection: readonly DocumentsRecycleBinItem[];
	allItems: readonly DocumentsRecycleBinItem[];
	isMobile: boolean;
	isEverythingSelected: boolean;
};

export type RecycleBinAction = Action<RecycleBinActionContext>;

type ActionHandlers = {
	recover: (documents: readonly DocumentsRecycleBinItem[]) => void;
	delete: (documents: readonly DocumentsRecycleBinItem[]) => void;
	selectAll: () => void;
	deselectAll: () => void;
};

function getRootActions(
	handlers: ActionHandlers
): readonly RecycleBinAction[] {

	return [
		{
			kind: ActionKind.Regular,
			name: "SelectAll",
			title: localize("_SelectAll"),
			icon: "layer-plus-check",
			canExecute: (c) => {
				return c.isMobile
					&& !c.isEverythingSelected;
			},
			execute: () => {
				handlers.selectAll();
			}
		},
		{
			kind: ActionKind.Regular,
			name: "DeselectAll",
			title: localize("_DeselectAll"),
			icon: "layer-xmark",
			canExecute: (c) => {
				return c.isMobile
					&& c.selection.length > 0;
			},
			execute: () => {
				handlers.deselectAll();
			}
		},
		{
			kind: ActionKind.Regular,
			name: "RecoverAll",
			title: localize("_RecoverAllItems"),
			icon: "arrow-rotate-left",
			canExecute: (c) => c.allItems.length > 0 && c.selection.length === 0,
			execute: (c) => handlers.recover(c.allItems)
		},
		{
			kind: ActionKind.Regular,
			name: "EmptyRecycleBin",
			title: localize("_EmptyRecycleBin"),
			icon: "trash-can",
			canExecute: (c) => c.allItems.length > 0 && c.selection.length === 0,
			execute: (c) => handlers.delete(c.allItems)
		}
	];
}

function getSelectionActions(
	handlers: ActionHandlers
): readonly RecycleBinAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "recover",
			title: localize("_Recover"),
			icon: "arrow-rotate-left",
			canExecute: (c) => c.selection.length > 0,
			execute: (c) => handlers.recover(c.selection)
		},
		{
			kind: ActionKind.Regular,
			name: "deleteFromTrashCan",
			title: localize("_Delete"),
			icon: "trash-can",
			canExecute: (c) => c.selection.length > 0,
			execute: (c) => handlers.delete(c.selection),
		}
	];
}
